/* StageStrip — what the agent becomes while it DRIVES the page.

   The dock / copilot panel is the wrong shape while NoVA scrolls, zooms,
   clicks and points: it hides a third of the screen she is talking about.
   Stage mode (ported 2026-10-17 from the Tradespace agent) swaps the panel
   for ONE low bottom band that leaves the page visible:

     [ transcript ticker (flex) ] [ control strip: orb · bars · state · Mute · End · ⤢ ]

   The strip owns the voice visualiser (barsRef) while it is on screen; the
   ticker is a three-line, bottom-pinned read of the live transcript so the
   person can follow along without the panel. `body.nv-stage-on` is toggled
   by the surface so the lens and other overlays can leave room for the band.

   Surfaces decide WHEN (auto on when a driving tool runs during a live
   voice session, auto off when the session ends, manual ⤢ / ⤓ sticks). */
import React, { useEffect, useMemo, useRef } from 'react';
import BrainThink, { summarizeThink, foldThink } from './BrainThink';
import './StageStrip.css';

// Mirrors AgentDock's visualiser bar count (the barsRef drives these spans).
const NUM_BARS = 20;

// The think stage, strip-sized: while NoVA drives the page the panel is
// gone, but the search still plays — the constellation + stamp in a low
// card above the ticker, resting on the last frame after the answer.
// Nothing is drawn that was not emitted; click = the full theatre.
// ([[every-answer-flows-through-the-brain]])
function ThinkCard({ think, onExpand }) {
  const events = think?.events;
  const caption = useMemo(() => (events?.length ? summarizeThink(foldThink(events, events.length - 1, () => null)) : ''), [events]);
  if (!events?.length) return null;
  return (
    <div className={`nv-stage-think${think.live ? ' is-live' : ''}`} role="group" aria-label="How NoVA is thinking" data-nova-ignore>
      <button type="button" className="nv-stage-think-open" onClick={onExpand} title="Open the full think stage">
        <span className="nv-stage-think-label">{think.live ? 'Thinking' : 'Thought'}</span>
        <span className="nv-stage-think-cap">{caption}</span>
        <span className="nv-stage-think-more" aria-hidden="true">⤢</span>
      </button>
      <div className="nv-stage-think-pic">
        <BrainThink graph={null} events={events} live={!!think.live} height={96} labels={false} rail={false} picker={false} stamp={false} />
      </div>
    </div>
  );
}

// Three lines: the previous agent turn (muted), what the user just said, and
// the live turn (streaming cursor). Pinned to the bottom like a teleprompter.
function Ticker({ prev, user, live, streaming }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [prev, user, live, streaming]);
  return (
    <div className="nv-stage-ticker" aria-live="polite" data-nova-ignore>
      <div ref={ref} className="nv-stage-ticker-box">
        {prev && <p className="nv-stage-ticker-prev">{prev}</p>}
        {user && <p className="nv-stage-ticker-user"><span className="nv-stage-ticker-you">you</span>{user}</p>}
        {live && <p className={`nv-stage-ticker-live${streaming ? ' nv-stage-ticker-streaming' : ''}`}>{live}{streaming ? '▍' : ''}</p>}
      </div>
    </div>
  );
}

// The pitch rail: where the story is while NoVA pitches (utils/pitchRunner
// pitchOutline()) — one chip per chapter (done / current / upcoming), the
// current node's path, "n of N", and Stop. A chip click is a spoken-style
// request ("Go to Pricing.") so the model stays in the loop and the next
// step still comes from the tools.
function PitchRail({ pitch, onJump, onStop }) {
  if (!pitch) return null;
  return (
    <nav className="nv-stage-pitch" aria-label="Pitch chapters" data-nova-ignore>
      <span className="nv-stage-pitch-pos">{pitch.position}</span>
      <ol className="nv-stage-pitch-chips">
        {pitch.chapters.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              className={`nv-stage-pitch-chip is-${c.status}`}
              aria-current={c.status === 'current' ? 'step' : undefined}
              onClick={() => onJump?.(c.title)}
              title={c.status === 'current' ? `Now: ${pitch.path.join(' › ')}` : `Go to ${c.title}`}
            >
              {c.title}
            </button>
          </li>
        ))}
      </ol>
      {pitch.path.length > 1 && <span className="nv-stage-pitch-path">{pitch.path.slice(1).join(' › ')}</span>}
      <button type="button" className="nv-stage-btn nv-stage-pitch-stop" onClick={onStop} title="Stop the pitch">Stop</button>
    </nav>
  );
}

export default function StageStrip({
  name = 'NoVA',
  live = false,          // voice session active
  voiceState = 'idle',   // listening | speaking | processing | thinking | connecting
  stateLabel = '',
  clock = '',
  muted = false,
  onToggleMute,
  onEnd,
  onExpand,
  barsRef = null,
  messages = [],         // [{ role, content }] in order
  streamingText = '',
  pitch = null,          // pitchOutline() while a pitch is running, else null
  onPitchJump,
  onPitchStop,
  think = null,          // { events, live } — the turn's think stream, else null
  onThinkExpand = null,  // click on the think card (default: onExpand)
}) {
  const lines = useMemo(() => {
    const list = Array.isArray(messages) ? messages : [];
    let lastA = -1;
    for (let i = list.length - 1; i >= 0; i -= 1) if (list[i].role === 'assistant') { lastA = i; break; }
    const lastU = list.map((m) => m.role).lastIndexOf('user');
    const user = lastU > lastA ? list[lastU].content : '';
    const assistants = list.filter((m) => m.role === 'assistant').map((m) => m.content);
    if (streamingText) return { prev: assistants[assistants.length - 1] || '', user, live: streamingText, streaming: true };
    return { prev: assistants[assistants.length - 2] || '', user, live: assistants[assistants.length - 1] || '', streaming: false };
  }, [messages, streamingText]);

  return (
    <>
      <div className={`nv-stage-strip${live ? ' nv-stage-live' : ''}`} role="toolbar" aria-label={`${name} controls (stage mode)`} data-nova-ignore>
        <span className={`nv-stage-orb nv-stage-orb-${voiceState}`} aria-hidden="true" />
        <div ref={barsRef} className="nv-stage-bars" aria-hidden="true">
          {Array.from({ length: NUM_BARS }).map((_, i) => <span key={i} className="nv-stage-bar" />)}
        </div>
        <span className="nv-stage-status" title={stateLabel}>
          {live ? stateLabel || 'Live' : name}{live && clock ? <span className="nv-stage-clock"> · {clock}</span> : null}
        </span>
        {live && (
          <button type="button" className={`nv-stage-btn${muted ? ' nv-stage-btn-on' : ''}`} onClick={onToggleMute} aria-pressed={muted} title={muted ? 'Unmute microphone' : 'Mute microphone'}>
            {muted ? 'Unmute' : 'Mute'}
          </button>
        )}
        {live && onEnd && (
          <button type="button" className="nv-stage-btn nv-stage-end" onClick={onEnd} title="End the voice session">End</button>
        )}
        <button type="button" className="nv-stage-btn nv-stage-expand" onClick={onExpand} title={`Expand ${name}`} aria-label={`Expand the ${name} panel`}>⤢</button>
      </div>
      <Ticker prev={lines.prev} user={lines.user} live={lines.live} streaming={lines.streaming} />
      <PitchRail pitch={pitch} onJump={onPitchJump} onStop={onPitchStop} />
      <ThinkCard think={think} onExpand={onThinkExpand || onExpand} />
    </>
  );
}
